import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail, RefreshCw, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Translations for login page
const TRANSLATIONS = {
  en: {
    login: "Login",
    loggingIn: "Logging in...",
    email: "Email",
    password: "Password",
    noAccount: "Don't have an account?",
    registerHere: "Register here",
    forgotPassword: "Forgot password?",
    emailNotVerified: "Email not verified",
    emailNotVerifiedMessage: "Please check your email and click the verification link to activate your account.",
    resendVerification: "Resend verification email",
    resending: "Sending...",
    resendSuccess: "Verification email sent! Check your inbox.",
    tooManyRequests: "Too many requests. Please wait before trying again.",
    back: "Back"
  },
  ms: {
    login: "Log Masuk",
    loggingIn: "Log masuk...",
    email: "Emel",
    password: "Kata Laluan",
    noAccount: "Tiada akaun?",
    registerHere: "Daftar di sini",
    forgotPassword: "Lupa kata laluan?",
    emailNotVerified: "Emel tidak disahkan",
    emailNotVerifiedMessage: "Sila semak emel anda dan klik pautan pengesahan untuk mengaktifkan akaun anda.",
    resendVerification: "Hantar semula emel pengesahan",
    resending: "Menghantar...",
    resendSuccess: "Emel pengesahan dihantar! Semak peti masuk anda.",
    tooManyRequests: "Terlalu banyak permintaan. Sila tunggu sebelum mencuba lagi.",
    back: "Kembali"
  },
  ta: {
    login: "உள்நுழை",
    loggingIn: "உள்நுழைகிறது...",
    email: "மின்னஞ்சல்",
    password: "கடவுச்சொல்",
    noAccount: "கணக்கு இல்லையா?",
    registerHere: "இங்கே பதிவு செய்யுங்கள்",
    forgotPassword: "கடவுச்சொல் மறந்துவிட்டதா?",
    emailNotVerified: "மின்னஞ்சல் சரிபார்க்கப்படவில்லை",
    emailNotVerifiedMessage: "உங்கள் கணக்கை செயல்படுத்த மின்னஞ்சலைச் சரிபார்த்து சரிபார்ப்பு இணைப்பைக் கிளிக் செய்யுங்கள்.",
    resendVerification: "சரிபார்ப்பு மின்னஞ்சலை மீண்டும் அனுப்பு",
    resending: "அனுப்புகிறது...",
    resendSuccess: "சரிபார்ப்பு மின்னஞ்சல் அனுப்பப்பட்டது! உங்கள் இன்பாக்ஸை சரிபார்க்கவும்.",
    tooManyRequests: "அதிக கோரிக்கைகள். மீண்டும் முயற்சிக்கும் முன் காத்திருக்கவும்.",
    back: "திரும்பு"
  },
  hi: {
    login: "लॉग इन करें",
    loggingIn: "लॉग इन हो रहा है...",
    email: "ईमेल",
    password: "पासवर्ड",
    noAccount: "खाता नहीं है?",
    registerHere: "यहां रजिस्टर करें",
    emailNotVerified: "ईमेल सत्यापित नहीं है",
    emailNotVerifiedMessage: "कृपया अपना ईमेल जांचें और अपना खाता सक्रिय करने के लिए सत्यापन लिंक पर क्लिक करें।",
    resendVerification: "सत्यापन ईमेल पुनः भेजें",
    resending: "भेज रहा है...",
    resendSuccess: "सत्यापन ईमेल भेजा गया! अपना इनबॉक्स जांचें।",
    tooManyRequests: "बहुत सारे अनुरोध। कृपया पुनः प्रयास करने से पहले प्रतीक्षा करें।",
    back: "वापस"
  },
  bn: {
    login: "লগ ইন",
    loggingIn: "লগ ইন হচ্ছে...",
    email: "ইমেল",
    password: "পাসওয়ার্ড",
    noAccount: "অ্যাকাউন্ট নেই?",
    registerHere: "এখানে নিবন্ধন করুন",
    emailNotVerified: "ইমেল যাচাই করা হয়নি",
    emailNotVerifiedMessage: "অনুগ্রহ করে আপনার ইমেল চেক করুন এবং আপনার অ্যাকাউন্ট সক্রিয় করতে যাচাইকরণ লিঙ্কে ক্লিক করুন।",
    resendVerification: "যাচাইকরণ ইমেল পুনরায় পাঠান",
    resending: "পাঠানো হচ্ছে...",
    resendSuccess: "যাচাইকরণ ইমেল পাঠানো হয়েছে! আপনার ইনবক্স চেক করুন।",
    tooManyRequests: "অনেক বেশি অনুরোধ। আবার চেষ্টা করার আগে অনুগ্রহ করে অপেক্ষা করুন।",
    back: "ফিরে যান"
  },
  ne: {
    login: "लग इन",
    loggingIn: "लग इन हुँदैछ...",
    email: "इमेल",
    password: "पासवर्ड",
    noAccount: "खाता छैन?",
    registerHere: "यहाँ दर्ता गर्नुहोस्",
    emailNotVerified: "इमेल प्रमाणित छैन",
    emailNotVerifiedMessage: "कृपया आफ्नो इमेल जाँच गर्नुहोस् र आफ्नो खाता सक्रिय गर्न प्रमाणीकरण लिंकमा क्लिक गर्नुहोस्।",
    resendVerification: "प्रमाणीकरण इमेल पुन: पठाउनुहोस्",
    resending: "पठाउँदैछ...",
    resendSuccess: "प्रमाणीकरण इमेल पठाइयो! आफ्नो इनबक्स जाँच गर्नुहोस्।",
    tooManyRequests: "धेरै अनुरोधहरू। कृपया पुन: प्रयास गर्नु अघि पर्खनुहोस्।",
    back: "फिर्ता"
  },
  my: {
    login: "လော့ဂ်အင်",
    loggingIn: "လော့ဂ်အင်ဝင်နေသည်...",
    email: "အီးမေးလ်",
    password: "စကားဝှက်",
    noAccount: "အကောင့်မရှိဘူးလား?",
    registerHere: "ဒီမှာ မှတ်ပုံတင်ပါ",
    emailNotVerified: "အီးမေးလ် အတည်မပြုရသေးပါ",
    emailNotVerifiedMessage: "သင့်အီးမေးလ်ကို စစ်ဆေးပြီး သင့်အကောင့်ကို အသက်သွင်းရန် အတည်ပြုလင့်ခ်ကို နှိပ်ပါ။",
    resendVerification: "အတည်ပြုအီးမေးလ် ပြန်ပို့ပါ",
    resending: "ပို့နေသည်...",
    resendSuccess: "အတည်ပြုအီးမေးလ် ပို့ပြီးပါပြီ! သင့် inbox ကို စစ်ဆေးပါ။",
    tooManyRequests: "တောင်းဆိုမှု များလွန်းသည်။ ထပ်မကြိုးစားမီ စောင့်ပါ။",
    back: "နောက်သို့"
  }
};

export default function LoginPage({ setUser }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [showVerificationError, setShowVerificationError] = useState(false);
  const navigate = useNavigate();
  
  const savedLang = localStorage.getItem('myshield_lang') || 'en';
  const t = TRANSLATIONS[savedLang] || TRANSLATIONS.en;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowVerificationError(false);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
      });

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      setUser(response.data.user);

      toast.success("Login successful!");
      
      if (response.data.user.role === "admin") {
        navigate("/admin");
      } else if (response.data.user.role === "staff") {
        navigate("/staff");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      // Check if it's an email verification error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes("not verified")) {
        setShowVerificationError(true);
      } else {
        toast.error(error.response?.data?.detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }
    
    setResendLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, { email });
      toast.success(t.resendSuccess);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(t.tooManyRequests);
      } else {
        toast.error(error.response?.data?.detail || "Failed to resend email");
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-[#0A1128] to-black p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>
      
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/")}
        className="absolute top-4 left-4 gap-2 z-10 text-white/60 hover:text-white hover:bg-white/10"
        data-testid="back-to-home-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        {t.back}
      </Button>
      
      <div className="w-full max-w-md glass-card p-8 relative z-10 animate-fade-in" data-testid="login-card">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
            alt="MyShield Logo" 
            className="h-20 w-auto mx-auto"
          />
        </div>
        
        {/* Email Not Verified Error */}
        {showVerificationError && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl animate-fade-in" data-testid="verification-error">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-400 mb-1">{t.emailNotVerified}</h3>
                <p className="text-sm text-white/60">{t.emailNotVerifiedMessage}</p>
                <Button
                  onClick={handleResendVerification}
                  variant="ghost"
                  size="sm"
                  className="mt-3 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 p-0 h-auto"
                  disabled={resendLoading}
                  data-testid="resend-verification-btn"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t.resending}
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {t.resendVerification}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-white/70">{t.email}</Label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setShowVerificationError(false); }}
              required
              data-testid="login-email-input"
              className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-white/70">{t.password}</Label>
              <Link to="/forgot-password" className="text-sm text-cyan-400 hover:text-cyan-300" data-testid="forgot-password-link">
                {t.forgotPassword}
              </Link>
            </div>
            <Input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
              className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
            />
          </div>
          <Button
            type="submit"
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 rounded-xl transition-all active:scale-[0.98]"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t.loggingIn}
              </>
            ) : (
              t.login
            )}
          </Button>
        </form>
        <div className="mt-6 text-center text-sm">
          <span className="text-white/50">{t.noAccount} </span>
          <Link to="/register" className="text-cyan-400 font-semibold hover:text-cyan-300" data-testid="register-link">
            {t.registerHere}
          </Link>
        </div>
      </div>
    </div>
  );
}
